/**
 * 24-Hour Line display formatting (docs/plans/oncall-line-plan.md 7.7, 5;
 * WP-03). Pure functions, no React and no network, so every tab formats a
 * number, a length or a time the same way.
 *
 *   maskPhone / last4     staff numbers are personal data (plan 5): everyone
 *                         who is not a line admin sees the last four digits
 *                         only. The server decides who gets a full number;
 *                         these only render what it sent.
 *   formatPhone           a full number for a line admin, "(555) 123-4567"
 *                         for a NANP number, the E.164 text otherwise
 *   formatDuration        a call's length as m:ss (h:mm:ss past an hour)
 *   formatDurationWords   the same length in words ("2 min 45 s"), for the
 *                         Settings tab's worst-case wait and screen readers
 *   formatDateTime,
 *   formatTime,
 *   formatDate            an instant in the SITE's timezone (plan 7.6: the
 *                         site's timezone is used for displayed times), never
 *                         the browser's
 *
 * Times are built from Intl formatToParts and joined with plain spaces, so
 * the output is the same on every ICU version (newer ones put a narrow
 * no-break space before AM/PM in format()). An unknown or missing timezone
 * falls back to UTC and the zone is then always named, so a wrong zone can
 * never pass for the site's own (plan 0.5: the safer reading).
 */

/** What every formatter answers for a missing value. */
export const EMPTY_VALUE = '—';

/** The masked prefix shown before the last four digits. */
const MASK_PREFIX = '•••-•••-';

// ---------------------------------------------------------------------------
// Phone numbers
// ---------------------------------------------------------------------------

function digitsOf(value: string | null | undefined): string {
  return typeof value === 'string' ? value.replace(/\D/g, '') : '';
}

/**
 * The last four digits of a number, or null when it has fewer than four. The
 * input may be a full E.164 number or the four digits the server sent a
 * non-admin.
 */
export function last4(value: string | null | undefined): string | null {
  const digits = digitsOf(value);
  return digits.length >= 4 ? digits.slice(-4) : null;
}

/**
 * A number with everything but its last four digits hidden: "•••-•••-4567".
 * Fewer than four digits shows the mask alone; no number shows the dash.
 */
export function maskPhone(value: string | null | undefined): string {
  const digits = digitsOf(value);
  if (!digits) return EMPTY_VALUE;
  const tail = last4(digits);
  return tail ? `${MASK_PREFIX}${tail}` : '••••';
}

/**
 * A full number for display. A NANP number ("+15551234567", "5551234567",
 * "1 (555) 123-4567") reads "(555) 123-4567"; anything else is returned
 * trimmed, as sent. Only line admins are sent full numbers (plan 5).
 */
export function formatPhone(value: string | null | undefined): string {
  if (typeof value !== 'string' || !value.trim()) return EMPTY_VALUE;
  const digits = digitsOf(value);
  const national = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (national.length === 10 && (value.trim().startsWith('+1') || !value.trim().startsWith('+'))) {
    return `(${national.slice(0, 3)}) ${national.slice(3, 6)}-${national.slice(6)}`;
  }
  return value.trim();
}

// ---------------------------------------------------------------------------
// Durations
// ---------------------------------------------------------------------------

function wholeSeconds(seconds: number | null | undefined): number | null {
  if (typeof seconds !== 'number' || !Number.isFinite(seconds) || seconds < 0) return null;
  return Math.round(seconds);
}

/** A length as m:ss, or h:mm:ss from an hour up: 45 -> "0:45", 134 -> "2:14", 3723 -> "1:02:03". */
export function formatDuration(seconds: number | null | undefined): string {
  const total = wholeSeconds(seconds);
  if (total === null) return EMPTY_VALUE;
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const ss = String(s).padStart(2, '0');
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${ss}`;
  return `${m}:${ss}`;
}

/** A length in words: 45 -> "45 s", 120 -> "2 min", 165 -> "2 min 45 s", 3723 -> "1 h 2 min 3 s". */
export function formatDurationWords(seconds: number | null | undefined): string {
  const total = wholeSeconds(seconds);
  if (total === null) return EMPTY_VALUE;
  if (total === 0) return '0 s';
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const parts: string[] = [];
  if (h > 0) parts.push(`${h} h`);
  if (m > 0) parts.push(`${m} min`);
  if (s > 0) parts.push(`${s} s`);
  return parts.join(' ');
}

// ---------------------------------------------------------------------------
// Times in the site's timezone
// ---------------------------------------------------------------------------

/** True when Intl accepts the IANA name. */
export function isValidTimeZone(timeZone: string | null | undefined): boolean {
  if (typeof timeZone !== 'string' || !timeZone.trim()) return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone });
    return true;
  } catch {
    return false;
  }
}

/** The site's timezone when valid, else UTC (and then the zone is always shown). */
export function resolveTimeZone(timeZone: string | null | undefined): { timeZone: string; fellBack: boolean } {
  return isValidTimeZone(timeZone) ? { timeZone: (timeZone as string).trim(), fellBack: false } : { timeZone: 'UTC', fellBack: true };
}

function toDate(value: string | number | Date | null | undefined): Date | null {
  if (value === null || value === undefined || value === '') return null;
  const d = value instanceof Date ? new Date(value.getTime()) : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

type PartMap = Partial<Record<Intl.DateTimeFormatPartTypes, string>>;

function partsOf(date: Date, timeZone: string, options: Intl.DateTimeFormatOptions): PartMap {
  const map: PartMap = {};
  for (const part of new Intl.DateTimeFormat('en-US', { ...options, timeZone }).formatToParts(date)) {
    if (part.type !== 'literal') map[part.type] = part.value;
  }
  return map;
}

export interface TimeFormatOptions {
  /** Include the year ("Sep 25, 2026"); default: only when it is not the current year in that zone. */
  year?: boolean | 'auto';
  /** Append the zone's short name ("EDT"); always on when the timezone fell back to UTC. */
  withZone?: boolean;
  /** "Now" for the 'auto' year rule; tests pass a fixed instant. */
  now?: Date;
}

function dateText(parts: PartMap, showYear: boolean): string {
  return showYear ? `${parts.month} ${parts.day}, ${parts.year}` : `${parts.month} ${parts.day}`;
}

function timeText(parts: PartMap): string {
  return `${parts.hour}:${parts.minute} ${(parts.dayPeriod || '').toUpperCase()}`.trim();
}

function wantYear(date: Date, timeZone: string, option: TimeFormatOptions['year'], now: Date | undefined): boolean {
  if (option === true) return true;
  if (option === false) return false;
  const thisYear = partsOf(now ?? new Date(), timeZone, { year: 'numeric' }).year;
  return partsOf(date, timeZone, { year: 'numeric' }).year !== thisYear;
}

function zoneText(date: Date, timeZone: string): string {
  return partsOf(date, timeZone, { timeZoneName: 'short' }).timeZoneName || timeZone;
}

const DATE_OPTIONS: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' };
const TIME_OPTIONS: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: '2-digit', hour12: true };

/** "Sep 25, 2:14 AM" in the site's zone ("Sep 25, 2026, 2:14 AM EDT" with year and zone). */
export function formatDateTime(
  value: string | number | Date | null | undefined,
  timeZone: string | null | undefined,
  options: TimeFormatOptions = {},
): string {
  const date = toDate(value);
  if (!date) return EMPTY_VALUE;
  const zone = resolveTimeZone(timeZone);
  const parts = partsOf(date, zone.timeZone, { ...DATE_OPTIONS, ...TIME_OPTIONS });
  const showYear = wantYear(date, zone.timeZone, options.year ?? 'auto', options.now);
  const text = `${dateText(parts, showYear)}, ${timeText(parts)}`;
  return options.withZone || zone.fellBack ? `${text} ${zoneText(date, zone.timeZone)}` : text;
}

/** "2:14 AM" in the site's zone ("2:14 AM EDT" with the zone). */
export function formatTime(
  value: string | number | Date | null | undefined,
  timeZone: string | null | undefined,
  options: Pick<TimeFormatOptions, 'withZone'> = {},
): string {
  const date = toDate(value);
  if (!date) return EMPTY_VALUE;
  const zone = resolveTimeZone(timeZone);
  const text = timeText(partsOf(date, zone.timeZone, TIME_OPTIONS));
  return options.withZone || zone.fellBack ? `${text} ${zoneText(date, zone.timeZone)}` : text;
}

/** "Sep 25" in the site's zone ("Sep 25, 2026" in another year, or with year: true). */
export function formatDate(
  value: string | number | Date | null | undefined,
  timeZone: string | null | undefined,
  options: Pick<TimeFormatOptions, 'year' | 'now'> = {},
): string {
  const date = toDate(value);
  if (!date) return EMPTY_VALUE;
  const zone = resolveTimeZone(timeZone);
  const parts = partsOf(date, zone.timeZone, DATE_OPTIONS);
  return dateText(parts, wantYear(date, zone.timeZone, options.year ?? 'auto', options.now));
}
